import React, { useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Btn } from '@/components/ui-ios';
import { api } from '@/lib/api';

/**
 * ChangePasswordDialog - self-service password rotation.
 *
 * Backend contract: POST /api/auth/me/change-password with
 *   { current_password, new_password, confirm_password }
 * The server re-validates everything (length, mismatch, current OK) so
 * the client-side checks here are just a UX nicety to avoid round-trips.
 *
 * On success we deliberately *don't* force a re-login: the user's JWT
 * stays valid (we didn't ask for the "logout other devices" feature),
 * so a successful change is a silent improvement plus a toast.
 */
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setShowCurrent(false);
    setShowNext(false);
    setShowConfirm(false);
  };

  const handleClose = (val) => {
    if (busy) return;
    if (!val) reset();
    onOpenChange(val);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    // Mirror the server-side rules so the user sees feedback instantly.
    if (!current) {
      toast.error('Inserisci la password attuale');
      return;
    }
    if (next.length < 8) {
      toast.error('La nuova password deve essere di almeno 8 caratteri');
      return;
    }
    if (next === current) {
      toast.error('La nuova password deve essere diversa da quella attuale');
      return;
    }
    if (next !== confirm) {
      toast.error('La conferma password non corrisponde');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/me/change-password', {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      toast.success('Password aggiornata con successo.');
      reset();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Errore durante il cambio password';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[440px]" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" /> Cambia password
          </DialogTitle>
          <DialogDescription>
            Inserisci la password attuale e la nuova password (minimo 8 caratteri).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <PwField
            label="Password attuale"
            value={current}
            onChange={setCurrent}
            show={showCurrent}
            setShow={setShowCurrent}
            autoComplete="current-password"
            testid="change-pw-current"
          />
          <PwField
            label="Nuova password"
            value={next}
            onChange={setNext}
            show={showNext}
            setShow={setShowNext}
            autoComplete="new-password"
            hint={next ? `${next.length}/8 caratteri minimi` : 'Almeno 8 caratteri'}
            hintTone={next.length >= 8 ? 'ok' : 'muted'}
            testid="change-pw-new"
          />
          <PwField
            label="Conferma nuova password"
            value={confirm}
            onChange={setConfirm}
            show={showConfirm}
            setShow={setShowConfirm}
            autoComplete="new-password"
            hint={
              confirm
                ? confirm === next
                  ? 'Le password coincidono'
                  : 'Le password non coincidono'
                : null
            }
            hintTone={confirm && confirm === next ? 'ok' : confirm ? 'err' : 'muted'}
            testid="change-pw-confirm"
          />

          <DialogFooter className="gap-2 sm:gap-2 pt-1">
            <Btn
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={busy}
              testid="change-pw-cancel"
            >
              Annulla
            </Btn>
            <Btn type="submit" disabled={busy} testid="change-pw-submit">
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Aggiornamento…
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4" /> Aggiorna password
                </>
              )}
            </Btn>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PwField({ label, value, onChange, show, setShow, autoComplete, hint, hintTone, testid }) {
  const toneClass =
    hintTone === 'ok'
      ? 'text-emerald-700'
      : hintTone === 'err'
      ? 'text-rose-700'
      : 'text-[color:var(--text-secondary)]';
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-[color:var(--text-secondary)]">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          data-testid={testid}
          className="w-full h-10 px-3 pr-10 rounded-xl border border-[color:var(--hairline)] bg-[color:var(--surface-1)] text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-transparent"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow(!show)}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-zinc-100 text-[color:var(--text-secondary)]"
          aria-label={show ? 'Nascondi password' : 'Mostra password'}
          data-testid={`${testid}-toggle`}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && <div className={`text-[11px] ${toneClass}`}>{hint}</div>}
    </div>
  );
}
